import { FC, useState, useEffect, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Status from 'src/components/Status';
import { formatCurrency } from 'src/utils/format.utils';
import { VoucherViewData } from 'src/models/Management.interface';
import { VoucherStatus } from 'src/models/enums/VoucherStatus.enum';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';
import {
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import ConfirmationDialogV2 from 'src/components/DialogBox/ConfirmationDialogV2/ConfirmationDialogV2';
import RejectDialogV2 from 'src/components/DialogBox/RejectDialogV2/RejectDialogV2';
import VoucherFormDialog from 'src/components/DialogBox/VoucherFormDialog/VoucherFormDialog';
import {
  voucherDialogTitles,
  voucherDialogSubtitles,
  apiUrls,
  routeURL,
  userRoles,
  dataStorageKeywords,
  voucherActionButtons,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import {
  StyledDialog,
  FixedHeaderWrapper,
  ContentWrapper,
  HeaderSection,
  HeaderLeft,
  HeaderRight,
  TitleTypography,
  CloseIcon,
  ActionMenuButton,
  MainContentGrid,
  LeftColumn,
  RightColumn,
  InfoRow,
  InfoBlock,
  InfoBlockRight,
  LabelTypography,
  ValueTypography,
  BillDetailsBox,
  BillRow,
  BillFieldBox,
  BillFieldBoxRight,
  AmountValueTypography,
  TotalPaidTypography,
  BalanceLabel,
  BalanceValue,
  CheckPaymentsButton,
  CheckPaymentsIcon,
  CheckPaymentsText,
  DescriptionBox,
  DescriptionNoteContainer,
  StatusInfoBox,
  StatusInfoRow,
  RejectionNoteBox,
  RejectionLabelTypography,
  SmallValueTypography,
  TotalBalanceLabel,
} from './VoucherViewDialog.styles';

interface VoucherViewDialogProps {
  open: boolean;
  onClose: () => void;
  data: VoucherViewData | null;
  onApprove?: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onVoucherPayments?: () => void;
  fetchVouchers?: () => void;
}

const VoucherViewDialog: FC<VoucherViewDialogProps> = ({
  open,
  onClose,
  data,
  onApprove,
  onReject,
  onEdit,
  onDelete,
  onVoucherPayments,
  fetchVouchers,
}) => {
  const navigate = useNavigate();
  const { showSnackbar } = StyledSnackbar();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const fetchUserRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setUserRole(role);
    };
    fetchUserRole();
  }, []);

  if (!data) return null;

  const isAdminOrOwner =
    userRole === userRoles.ADMIN || userRole === userRoles.OWNER;

  const isPendingApproval = data.status === VoucherStatus.PENDING_APPROVAL;
  const isApproved = data.status === VoucherStatus.APPROVED;
  const isRejected = data.status === VoucherStatus.REJECTED;
  const isPaymentProgress = data.status === VoucherStatus.PAYMENT_PROGRESS;
  const isPaymentCompleted = data.status === VoucherStatus.PAYMENT_COMPLETED;

  const showApproveReject = isPendingApproval && isAdminOrOwner;
  const showEditMenu = isPendingApproval;
  const showDeleteMenu = isPendingApproval;
  const showPaymentsMenu =
    isApproved || isPaymentProgress || isPaymentCompleted;

  const hasPaymentInfo = isPaymentProgress || isPaymentCompleted;
  const showCheckPayment = isApproved || isPaymentProgress;
  const totalPaidAmount = data.totalPaidAmount ?? 0;
  const balanceToBePaid = data.billAmount - totalPaidAmount;

  const hasMenuItems =
    showEditMenu || showApproveReject || showDeleteMenu || showPaymentsMenu;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleMenuClose();
    if (onEdit) {
      onEdit();
    } else {
      setEditDialogOpen(true);
    }
  };

  const handleApproveClick = () => {
    handleMenuClose();
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (approveLoading) return;
    setApproveLoading(true);
    try {
      const response = await axiosTokenInstance.patch(apiUrls.VOUCHER_STATUS, {
        id: data.id,
        status: 'Approved',
      });
      showSnackbar(
        response.data.message || 'Voucher approved successfully',
        'success'
      );
      setApproveDialogOpen(false);
      if (onApprove) onApprove();
      if (fetchVouchers) fetchVouchers();
      onClose();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setApproveLoading(false);
    }
  };

  const handleRejectClick = () => {
    handleMenuClose();
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (rejectLoading) return;
    setRejectLoading(true);
    try {
      const response = await axiosTokenInstance.patch(apiUrls.VOUCHER_STATUS, {
        id: data.id,
        status: 'Rejected',
        rejectionNotes: reason,
      });
      showSnackbar(
        response.data.message || 'Voucher rejected successfully',
        'success'
      );
      setRejectDialogOpen(false);
      if (onReject) onReject();
      if (fetchVouchers) fetchVouchers();
      onClose();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setRejectLoading(false);
    }
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    if (onDelete) {
      onDelete();
    } else {
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteLoading) return;
    setDeleteLoading(true);
    try {
      const response = await axiosTokenInstance.delete(apiUrls.DELETE_VOUCHER, {
        params: { id: data.id },
      });
      showSnackbar(
        response.data.message || 'Voucher deleted successfully',
        'success'
      );
      setDeleteDialogOpen(false);
      if (fetchVouchers) fetchVouchers();
      onClose();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePaymentsClick = () => {
    handleMenuClose();
    if (onVoucherPayments) {
      onVoucherPayments();
    } else if (data) {
      onClose();
      navigate(routeURL.VOUCHER_PAYMENTS_REDIRECT(data.id), {
        state: { billNumber: data.billNumber },
      });
    }
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    if (fetchVouchers) fetchVouchers();
    onClose();
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      {/* Fixed Header Section */}
      <FixedHeaderWrapper>
        <HeaderSection>
          <HeaderLeft>
            <TitleTypography>Voucher Details</TitleTypography>
            <Status status={data.status} />
          </HeaderLeft>
          <HeaderRight>
            <ActionMenuButton
              id="view-voucher-action-menu-button"
              aria-controls={menuOpen ? 'view-voucher-action-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? 'true' : undefined}
              onClick={hasMenuItems ? handleMenuClick : undefined}
              component="button"
            >
              <i className="bx bx-dots-horizontal-rounded"></i>
            </ActionMenuButton>
            <CloseIcon
              component="i"
              className="bx bx-x-circle"
              onClick={onClose}
            />
          </HeaderRight>
        </HeaderSection>
      </FixedHeaderWrapper>

      {/* Scrollable Content */}
      <ContentWrapper>
        <MainContentGrid>
          {/* Left Column */}
          <LeftColumn>
            {/* Payee & Created Date Row */}
            <InfoRow>
              <InfoBlock>
                <LabelTypography>Payee</LabelTypography>
                <ValueTypography>{data.payee}</ValueTypography>
                <LabelTypography>Voucher type</LabelTypography>
                <ValueTypography>{data.voucherType}</ValueTypography>
              </InfoBlock>
              <InfoBlockRight>
                <LabelTypography>Created Date</LabelTypography>
                <ValueTypography>{formatDate(data.createdAt)}</ValueTypography>
              </InfoBlockRight>
            </InfoRow>

            {/* Bill Details Box */}
            <BillDetailsBox>
              <BillRow>
                <BillFieldBox>
                  <LabelTypography>Bill Number</LabelTypography>
                  <ValueTypography>{data.billNumber}</ValueTypography>
                </BillFieldBox>
                <BillFieldBoxRight>
                  <LabelTypography>Bill Amount</LabelTypography>
                  <AmountValueTypography>
                    {formatCurrency(data.billAmount)}
                  </AmountValueTypography>
                </BillFieldBoxRight>
              </BillRow>

              {hasPaymentInfo && (
                <BillRow>
                  <BillFieldBox>
                    <TotalBalanceLabel>Total Paid Amount</TotalBalanceLabel>
                    <TotalPaidTypography>
                      {formatCurrency(totalPaidAmount)}
                    </TotalPaidTypography>
                  </BillFieldBox>
                  <BillFieldBoxRight>
                    {isPaymentCompleted ? (
                      <CheckPaymentsButton onClick={handlePaymentsClick}>
                        <CheckPaymentsIcon
                          component="i"
                          className="bx bx-dollar-circle"
                        />
                        <CheckPaymentsText>Check Payments</CheckPaymentsText>
                      </CheckPaymentsButton>
                    ) : (
                      <>
                        <BalanceLabel>Balance to be paid</BalanceLabel>
                        <BalanceValue>
                          {formatCurrency(balanceToBePaid)}
                        </BalanceValue>
                      </>
                    )}
                  </BillFieldBoxRight>
                </BillRow>
              )}

              {showCheckPayment && (
                <CheckPaymentsButton onClick={handlePaymentsClick}>
                  <CheckPaymentsIcon
                    component="i"
                    className="bx bx-dollar-circle"
                  />
                  <CheckPaymentsText>Check Payments</CheckPaymentsText>
                </CheckPaymentsButton>
              )}
            </BillDetailsBox>

            {/* Description Box - Expandable */}
            <DescriptionBox>
              <DescriptionNoteContainer>
                <LabelTypography>Note</LabelTypography>
                <ExpandableText
                  text={data.description}
                  lines={2}
                  placeholder="N/A"
                />
              </DescriptionNoteContainer>
            </DescriptionBox>
          </LeftColumn>

          {/* Right Column - Status Info */}
          <RightColumn>
            {isRejected ? (
              <StatusInfoBox>
                <StatusInfoRow>
                  <InfoBlock>
                    <LabelTypography>Rejected Date</LabelTypography>
                    <SmallValueTypography>
                      {formatDate(data.approvedDate)}
                    </SmallValueTypography>
                  </InfoBlock>
                  <InfoBlockRight>
                    <LabelTypography>Rejected User</LabelTypography>
                    <SmallValueTypography>
                      {data.approvedBy || 'N/A'}
                    </SmallValueTypography>
                  </InfoBlockRight>
                </StatusInfoRow>
                <RejectionNoteBox>
                  <RejectionLabelTypography>
                    Rejection Note
                  </RejectionLabelTypography>
                  <ValueTypography>
                    {data.rejectionNotes || 'N/A'}
                  </ValueTypography>
                </RejectionNoteBox>
              </StatusInfoBox>
            ) : (
              (isApproved || isPaymentProgress || isPaymentCompleted) && (
                <StatusInfoBox>
                  <StatusInfoRow>
                    <InfoBlock>
                      <LabelTypography>Approved Date</LabelTypography>
                      <SmallValueTypography>
                        {formatDate(data.approvedDate)}
                      </SmallValueTypography>
                    </InfoBlock>
                    <InfoBlockRight>
                      <LabelTypography>Approved User</LabelTypography>
                      <SmallValueTypography>
                        {data.approvedBy || 'N/A'}
                      </SmallValueTypography>
                    </InfoBlockRight>
                  </StatusInfoRow>
                </StatusInfoBox>
              )
            )}
          </RightColumn>
        </MainContentGrid>
      </ContentWrapper>

      {/* Action Menu Popover */}
      <ActionMenuPopover
        width="200px"
        id="view-voucher-action-menu"
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
      >
        <MenuItemsWrapper>
          {showEditMenu && (
            <MenuItemWithIcon
              onClick={handleEditClick}
              iconClassName="bx bx-edit"
              text={voucherActionButtons.EDIT}
            />
          )}

          {showApproveReject && (
            <>
              <MenuItemWithIcon
                onClick={handleApproveClick}
                iconClassName="bx bx-check"
                text={voucherActionButtons.APPROVE}
              />
              <MenuItemWithIcon
                onClick={handleRejectClick}
                iconClassName="bx bx-x"
                text={voucherActionButtons.REJECT}
              />
            </>
          )}

          {showDeleteMenu && (
            <MenuItemWithIcon
              onClick={handleDeleteClick}
              iconClassName="bx bx-trash"
              text={voucherActionButtons.DELETE}
            />
          )}

          {showPaymentsMenu && (
            <MenuItemWithIcon
              onClick={handlePaymentsClick}
              iconClassName="bx bx-money"
              text={voucherActionButtons.PAYMENTS}
            />
          )}
        </MenuItemsWrapper>
      </ActionMenuPopover>

      {/* Edit Voucher Dialog */}
      <VoucherFormDialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        onSuccess={handleEditSuccess}
        data={data}
        mode="edit"
      />

      {/* Approve Confirmation Dialog */}
      <ConfirmationDialogV2
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={voucherDialogTitles.APPROVE}
        subtitle={voucherDialogSubtitles.APPROVE}
        subtitleSecondary={voucherDialogSubtitles.APPROVE_SECONDARY}
        confirmText={voucherActionButtons.APPROVE}
        confirmButtonColor="var(--success)"
        iconColor="var(--warning)"
      />

      {/* Reject Dialog */}
      <RejectDialogV2
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
        title={voucherDialogTitles.REJECT}
        subtitle={voucherDialogSubtitles.REJECT}
        subtitleSecondary={voucherDialogSubtitles.REJECT_SECONDARY}
        placeholder="Reason for rejection"
        confirmText={voucherActionButtons.REJECT}
        loading={rejectLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialogV2
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={voucherDialogTitles.DELETE}
        subtitle={voucherDialogSubtitles.DELETE}
        subtitleSecondary={voucherDialogSubtitles.DELETE_SECONDARY}
        confirmText={voucherActionButtons.DELETE}
        confirmButtonColor="var(--error)"
        iconColor="var(--error)"
      />
    </StyledDialog>
  );
};

export default VoucherViewDialog;
